import { inject, injectable } from "@launchtray/tsyringe-async";
import { IPlayerModel } from "../../../../common/models/IPlayerModel";
import { IFortuneWheelController } from "./IFortuneWheelController";
import { IFortuneWheelModel } from "../../model/IFortuneWheelModel";
import { WedgeRewardType } from "../../model/WedgeRewardType";
import { WedgeRewardData } from "../../model/WedgeRewardData";
import { PaymentResult } from "../../../../../../web3/PaymentResult";
import { AsyncOperationNotification } from "../../../../common/components/AsyncOperationNotification";
import { IContextResolver } from "by/gamefactory/cityversetycoon/composition/IContextResolver";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { DefaultMessagesSceneFactory } from "by/gamefactory/cityversetycoon/scenes/SceneFactories";
import { CancellationToken } from "async/CancellationToken";
import { SceneManager } from "by/gamefactory/scene/SceneManager";
import { Erc20PaymentProcessingModule } from "../../../ethL1paymentprocessing/Erc20PaymentProcessingModule";
import { PaymentProcessingType } from "../../../ethL1paymentprocessing/type/PaymentProcessingType";
import { PaymentProcessingPopupSceneOptions } from "../../../ethL1paymentprocessing/scenes/PaymentProcessingPopupSceneOptions";
import { Web3Config } from "by/gamefactory/web3/config/Web3Config";

@injectable()
export class FortuneWheelController implements IFortuneWheelController {
    private readonly sceneManager: SceneManager;
    private readonly playerModel: IPlayerModel;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly web3Model: Web3Model;
    private readonly web3Config: Web3Config;
    private readonly contextResolver: IContextResolver;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    constructor(
        @inject("IContextResolver") contextResolver: IContextResolver,
        sceneManager: SceneManager,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IFortuneWheelModel") fortuneWheelModel: IFortuneWheelModel,
        web3Model: Web3Model,
        web3Config: Web3Config,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("ILogger") logger: ILogger
    ) {
        this.sceneManager = sceneManager;
        this.playerModel = playerModel;
        this.fortuneWheelModel = fortuneWheelModel;
        this.web3Model = web3Model;
        this.web3Config = web3Config;
        this.contextResolver = contextResolver;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
    }

    public async updateFortuneWheelStatus(): Promise<void> {
        await this.fortuneWheelModel.updateFortuneWheelStatus();
    }

    public async claimHourlyBonusReward(): Promise<WedgeRewardData> {
        try {
            await this.fortuneWheelModel.claimHourlyBonusReward();
            const rewardWedgeId = this.fortuneWheelModel.rewardWedgeId.value;
            if ((rewardWedgeId != undefined && rewardWedgeId != "")) {
                const rewardIndex = this.fortuneWheelModel.rewardWedgeIndex;
                const rewardData = this.fortuneWheelModel.getWedgeRewardData(this.fortuneWheelModel.wedges.value[rewardIndex]);
                switch (rewardData.type) {
                    case WedgeRewardType.Coin: this.playerModel.addCoins(rewardData.count); break;
                    case WedgeRewardType.DiceRoll: this.playerModel.addRolls(rewardData.count); break;
                    case WedgeRewardType.Shield: this.playerModel.addShields(rewardData.count); break;
                }
                return rewardData;
            } else {
                this.playerModel.addRolls(this.fortuneWheelModel.hourlyBonusItems.value[this.fortuneWheelModel.currentHourlyBonusSection.value].freeDiceRollsReward);
                return null;
            }
        }
        catch (e: unknown) {
            this.logger.error("Exception on claim hourly bonus reward: ", e);
        }
    }

    public async getClaimHourlyBonusReward(): Promise<number> {
        try {
            await this.fortuneWheelModel.claimHourlyBonusReward();
            const rewardWedgeId = this.fortuneWheelModel.rewardWedgeId.value;
            if ((rewardWedgeId != undefined && rewardWedgeId != "")) {
                const rewardIndex = this.fortuneWheelModel.rewardWedgeIndex;
                const rewardData = this.fortuneWheelModel.getWedgeRewardData(this.fortuneWheelModel.wedges.value[rewardIndex]);
                return rewardData.count;
            } else {
                return this.fortuneWheelModel.hourlyBonusItems.value[this.fortuneWheelModel.currentHourlyBonusSection.value].freeDiceRollsReward;
            }
        }
        catch (e: unknown) {
            this.logger.error("Exception on claim hourly bonus reward: ", e);
        }
    }

    public async loadGoldenWheelStatus(): Promise<void> {
        await this.fortuneWheelModel.goldenWheelModel.value.loadGoldenWheelStatus();
    }

    public async claimGoldenWheelReward(): Promise<WedgeRewardData> {
        const goldenWheelModel = this.fortuneWheelModel.goldenWheelModel.value;
        try {
            this.asyncOperationNotification.show();
            await goldenWheelModel.claimGoldenWheelReward();
            const rewardIndex = goldenWheelModel.rewardWedgeIndex;
            const rewardData = goldenWheelModel.getWedgeRewardData(goldenWheelModel.wedges.value[rewardIndex]);
            switch (rewardData.type) {
                case WedgeRewardType.Coin: this.playerModel.addCoins(rewardData.count); break;
                case WedgeRewardType.DiceRoll: this.playerModel.addRolls(rewardData.count); break;
                case WedgeRewardType.Shield: this.playerModel.addShields(rewardData.count); break;
            }
            return rewardData;
        }
        catch (e: unknown) {
            this.logger.error("Exception on claim hourly bonus reward: ", e);
        }
        finally {
            this.asyncOperationNotification.hide();
        }
    }

    public async loadPiggyWheelStatus(): Promise<void> {
        await this.fortuneWheelModel.piggyWheelModel.value.loadPiggyWheelStatus();
    }

    public async claimPiggyWheelReward(): Promise<WedgeRewardData> {
        const piggyWheelModel = this.fortuneWheelModel.piggyWheelModel.value;
        await piggyWheelModel.claimPiggyWheelReward();
        const rewardIndex = piggyWheelModel.rewardWedgeIndex;
        const rewardData = piggyWheelModel.getWedgeRewardData(piggyWheelModel.wedges.value[rewardIndex]);
        switch (rewardData.type) {
            case WedgeRewardType.Coin: this.playerModel.addCoins(rewardData.count); break;
            case WedgeRewardType.DiceRoll: this.playerModel.addRolls(rewardData.count); break;
            case WedgeRewardType.Shield: this.playerModel.addShields(rewardData.count); break;
        }
        return rewardData;
    }

    public async buyGoldenWheel(tokenId: string, price: number, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            this.asyncOperationNotification.show();
            const baseErc20PaymentProcessingModule = await this.contextResolver.resolve(Erc20PaymentProcessingModule)
            const tokenSymbol = await this.web3Model.getTokenSymbol(this.web3Config.targetChain, this.web3Config.reservoirDevUsdcCurrency);
            this.asyncOperationNotification.hide();
            return await baseErc20PaymentProcessingModule.start({
                chain: this.web3Config.targetChain,
                erc20Address: this.web3Config.reservoirDevUsdcCurrency,
                tokenSymbol, 
                type: PaymentProcessingType.STORE_PACKAGE,
                price,
                tokenId
                } as PaymentProcessingPopupSceneOptions<PaymentProcessingType.STORE_PACKAGE>);
        } catch(e: unknown) {
            this.logger.error("Exception processing usdc payment popup for golden wheel offer: ", e);
            return PaymentResult.FAILURE;
        } finally {
            this.asyncOperationNotification.hide();
        }
    }

    private async showPaymentMessage(operation: Promise<PaymentResult>, cancellationToken: CancellationToken): Promise<PaymentResult> {
        try {
            this.asyncOperationNotification.show();
            const factory: DefaultMessagesSceneFactory = await this.contextResolver.resolve("DefaultMessagesSceneFactory");
            const scene = await factory({ operation, operationCancellationToken: cancellationToken, withProcessingPage: true });
            this.asyncOperationNotification.hide();
            if (cancellationToken.isCancellationRequested) {
                scene.view.destroy();
                return PaymentResult.CANCELLED;
            }
            return await this.sceneManager.showViewableAsync(scene) as PaymentResult;
        }
        catch (e: unknown) {
            this.asyncOperationNotification.hide();
            this.logger.error("Exception on showing payment message: ", e);
            return PaymentResult.FAILURE;
        }
    }
}
